/* ============================================================================
   Liquid core - hollie's interior, in layered 2D radial gradients
   ----------------------------------------------------------------------------
   A handful of palette blobs drift on incommensurate sine orbits, the whole
   field sways, and a slow tide of light washes across, all over a retintable
   base: her liquid, painted with layered radial gradients per docs/DESIGN.md
   06. Ported from the brand guide's lightweight liquid core
   (marketing-website/src/lib/liquid-ether.ts), the deliberately light stand-in
   for the THREE.js Navier-Stokes sim in components/common/LiquidEther.tsx. The
   orb drives energy through the same hooks the sim had: simOptions.mouse_force
   (voice), setAutoSpeed (connecting), setBgColor (state tint), step (frame
   clock). Keep this mirrored with the brand guide so the two never diverge.
   ========================================================================== */

/** Options for {@link mountLiquidCore}. */
export interface LiquidCoreOptions {
  /** Current palette, painted as soft drifting blobs. */
  colors?: string[];
  /** Opaque base fill; retintable per frame via setBgColor. */
  bgColor?: string;
  /** Drift clock rate (0.1 is calm). */
  autoSpeed?: number;
  /** Energy baseline: travel and brightness of the currents (raised with voice). */
  mouseForce?: number;
  /** Brightness of the slow light tide across the liquid. 0 is off. */
  washAmp?: number;
  /** Centre lens, 0..~0.3: blobs lean toward the middle. */
  bulge?: number;
  /** Skip the internal rAF loop; the caller drives frames via step(). */
  externalLoop?: boolean;
}

/** Imperative handle returned by {@link mountLiquidCore}. */
export interface LiquidCoreHandle {
  /** The canvas painted into; the orb blits this when the dots split. */
  canvas: HTMLCanvasElement;
  /** Energy lever: raise mouse_force with the voice to push the currents harder. */
  simOptions: { mouse_force: number };
  /** Set the drift clock rate. */
  setAutoSpeed: (v: number) => void;
  /** Retint the base per frame (0-255 channels). */
  setBgColor: (r: number, g: number, b: number) => void;
  /** Advance one frame; t is a rAF timestamp in ms. */
  step: (t: number) => void;
  /** Stop any internal loop and remove the canvas. */
  destroy: () => void;
}

/* Internal paint resolution. The canvas upscales to the mount via CSS, and that
   upscaling is the final soften: at 144px the gradient layers fuse into one
   liquid instead of reading as separate circles. */
const SIZE = 144;
const GOLDEN = 2.399963; // golden angle: spreads blob anchors without repeats

type RGB = [number, number, number];

/** Parse a #rrggbb hex string to an RGB triplet. */
function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
/** Format an RGB triplet with alpha as an rgba() string. */
function rgba(c: RGB, a: number): string {
  return 'rgba(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ',' + a.toFixed(3) + ')';
}
/** Clamp a value into [lo, hi]. */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** One drifting palette blob with its incommensurate orbit. */
interface Blob {
  color: RGB;
  bx: number; by: number;              // anchor offset from centre
  r0: number;                          // resting radius (canvas fraction)
  a0: number;                          // resting alpha
  f1: number; f2: number; f3: number;  // incommensurate drift frequencies
  p1: number; p2: number; p3: number;  // phases
}

/**
 * Mount the liquid core into `container`. Returns an imperative handle the orb
 * drives frame by frame. Safe in non-canvas environments: when getContext('2d')
 * is unavailable (server, jsdom), step() is a no-op rather than throwing.
 */
export function mountLiquidCore(container: HTMLElement, options: LiquidCoreOptions = {}): LiquidCoreHandle {
  const {
    colors = ['#5227FF', '#9D5CFF', '#FF9FFC', '#B19EEF'],
    bgColor = '#8672CC',
    autoSpeed = 0.1,
    mouseForce = 14,
    washAmp = 0.04,
    bulge = 0,
    externalLoop = false,
  } = options;

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  container.style.position = container.style.position || 'relative';
  container.style.overflow = container.style.overflow || 'hidden';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');

  // Deterministic per-blob orbits (golden-angle anchors, frequencies that never
  // sync), so every orb is alive the same way without a random seed. Dark
  // colours carry more weight (bigger, more opaque) than light ones, so the
  // deep-volt currents stay legible instead of washing into the base.
  const blobs: Blob[] = colors.map((c, i) => {
    const g = i * GOLDEN;
    const rgb = hexToRgb(c);
    const lum = 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
    const dark = lum < 170;
    return {
      color: rgb,
      bx: 0.26 * Math.cos(g),
      by: 0.26 * Math.sin(g),
      r0: (dark ? 0.38 : 0.30) + 0.04 * (i % 3),
      a0: dark ? 0.74 : 0.50,
      f1: 0.50 + 0.13 * i,
      f2: 0.36 + 0.09 * ((i + 2) % 5),
      f3: 0.23 + 0.07 * i,
      p1: g * 1.7, p2: g * 0.9, p3: g * 2.3,
    };
  });

  const simOptions = { mouse_force: mouseForce };
  const baseForce = Math.max(1, mouseForce);
  let speed = autoSpeed;
  let bg: RGB = hexToRgb(bgColor);
  let clock = 0;
  let lastT: number | null = null;
  let raf = 0;
  let destroyed = false;

  function draw(energy: number): void {
    if (!ctx) return;
    // Her base, retinted per frame by the orb's state palette.
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = rgba(bg, 1);
    ctx.fillRect(0, 0, SIZE, SIZE);

    // The whole field sways: one slow rotation every blob offset rides.
    const sway = Math.sin(clock * 0.21) * 0.5;
    const cs = Math.cos(sway), sn = Math.sin(sway);
    const travel = 0.20 * (0.7 + 0.6 * energy);
    const lean = 1 - clamp(bulge, 0, 0.5);

    for (const b of blobs) {
      const ox = b.bx + (Math.sin(clock * b.f1 + b.p1) * 0.6 + Math.sin(clock * b.f3 + b.p3) * 0.4) * travel;
      const oy = b.by + (Math.cos(clock * b.f2 + b.p2) * 0.6 + Math.sin(clock * b.f3 * 1.3 + b.p2) * 0.4) * travel;
      const px = (0.5 + (ox * cs - oy * sn) * lean) * SIZE;
      const py = (0.5 + (ox * sn + oy * cs) * lean) * SIZE;
      const rr = (b.r0 + 0.06 * Math.sin(clock * b.f2 * 1.7 + b.p3)) * SIZE * (1 + 0.18 * (energy - 1));
      const alpha = clamp(b.a0 * (0.85 + 0.3 * (energy - 1)), 0, 0.9);
      const grad = ctx.createRadialGradient(px, py, 0, px, py, Math.max(2, rr));
      grad.addColorStop(0, rgba(b.color, alpha));
      grad.addColorStop(0.55, rgba(b.color, alpha * 0.45));
      grad.addColorStop(1, rgba(b.color, 0));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, SIZE, SIZE);
    }

    // The living wash: a slow tide of light drifting across the still water.
    if (washAmp > 0) {
      const a = clock * 0.13;
      const dx = Math.cos(a) * SIZE * 0.5, dy = Math.sin(a) * SIZE * 0.5;
      const tide = ctx.createLinearGradient(SIZE / 2 - dx, SIZE / 2 - dy, SIZE / 2 + dx, SIZE / 2 + dy);
      const lift = washAmp * (0.5 + 0.5 * Math.sin(clock * 0.4));
      tide.addColorStop(0, 'rgba(255,255,255,0)');
      tide.addColorStop(1, 'rgba(255,255,255,' + lift.toFixed(3) + ')');
      ctx.fillStyle = tide;
      ctx.fillRect(0, 0, SIZE, SIZE);
    }
  }

  function step(t: number): void {
    if (destroyed) return;
    const dt = lastT === null ? 0 : clamp((t - lastT) / 1000, 0, 0.1);
    lastT = t;
    // Energy: the same lever the sim's mouse force was. Her voice pushes the
    // currents harder and a touch faster, square-rooted so it never thrashes.
    const energy = Math.sqrt(Math.max(0.2, simOptions.mouse_force / baseForce));
    clock += dt * speed * 10 * (0.6 + 0.4 * energy);
    draw(energy);
  }

  if (!externalLoop) {
    const loop = (t: number) => { step(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
  }

  return {
    canvas,
    simOptions,
    setAutoSpeed: (v: number) => { speed = v; },
    setBgColor: (r: number, g: number, b: number) => { bg = [clamp(r, 0, 255), clamp(g, 0, 255), clamp(b, 0, 255)]; },
    step,
    destroy: () => { destroyed = true; if (raf) cancelAnimationFrame(raf); canvas.remove(); },
  };
}
